import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Spinner } from 'react-bootstrap';
import { listGlobalCropTypes, CropTypeNode } from '../../services/cropTypeService';
import { lineageOf } from '../../utils/cropLineage';
import { useMenu } from '../../auth/useMenu';
import * as Sentry from '@sentry/react';
import {
  sprayService,
  SpraySession,
  SprayProgram,
  SprayRound,
  SprayMachine,
  SprayOperator,
  SprayProduct,
  Orchard,
  OperatorSpraySessionCombo,
  IntensityLabel,
} from '../../services/sprayService';
import OrchardPicker from './OrchardPicker';
import ComboListEditor, { ComboInput } from './ComboListEditor';
import ComboInstructionCell from './ComboInstructionCell';
import PlanningSummary from './PlanningSummary';
import PageHeader from '../shell/PageHeader';
import {
  Field,
  FormSection,
  FormShell,
  FormShellSection,
  Modal,
  Notice,
  SaveBar,
  SaveBarAction,
  SaveBarStatus,
} from '../patterns';
import { STATUS_VARIANT, STATUS_I18N_KEY, TERMINAL_STATUSES, TERMINAL_ISH_STATUSES } from '../../utils/spraySessionStatus';
import { formatLitres } from '../../utils/planningSummaryFormat';
import { canEditTerminalSpraySession } from '../../utils/spraySessionPermissions';
import { isIntensityOnMachineCalibration } from '../../utils/isIntensityOnMachineCalibration';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';

interface Props {
  session?: SpraySession;
  prefilledProgramId?: string;
  onSave: (result: SpraySession | SpraySession[]) => void;
  onCancel: () => void;
}

/**
 * Build the Round dropdown's option label per spec
 * (spray_session_ui_changes_20260502 / Q7 of design grill):
 *   new + named:   `R{seq} — {name} ({plannedDate})`
 *   new + unnamed: `R{seq} ({plannedDate})`
 *   legacy + named: `{name} ({plannedDate})`
 *   legacy + unnamed: `{plannedDate}`
 */
function formatRoundOptionLabel(r: SprayRound): string {
  const datePart = `(${r.plannedDate})`;
  if (typeof r.seq === 'number') {
    return r.name ? `R${r.seq} — ${r.name} ${datePart}` : `R${r.seq} ${datePart}`;
  }
  return r.name ? `${r.name} ${datePart}` : r.plannedDate;
}

/**
 * Sort rounds by seq ascending; legacy rounds (seq undefined) sink to the end.
 */
function sortRounds(rounds: SprayRound[]): SprayRound[] {
  return [...rounds].sort((a, b) => (a.seq ?? Infinity) - (b.seq ?? Infinity));
}

/**
 * Canonical session-name composition (track
 * spray_session_ui_changes_20260502, Phase 3 refinement after manual
 * verification). The intended form is
 *   `${programName} - ${roundName} - ${orchardName}`
 * with empty/undefined segments dropped — so a round without a name on
 * disk collapses to `${programName} - ${orchardName}`, and a not-yet-
 * picked orchard collapses to `${programName} - ${roundName}`. Used by
 * the program/round auto-fills in create mode and by the orchard-change
 * rename modal in edit mode.
 */
function composeSessionName(parts: Array<string | undefined>): string {
  return parts
    .map(p => p?.trim())
    .filter((p): p is string => !!p)
    .join(' - ');
}

function renderWaterVolumeReadout(
  t: (key: string, opts?: Record<string, unknown>) => string,
  orchardIds: string[],
  computed: number | null,
  waterPerHectare: number | undefined,
  areaHectares: number | undefined,
): React.ReactNode {
  if (orchardIds.length > 1) {
    return <div className="text-muted small">{t('spray.sessions.waterVolumeReadout.deferredMulti')}</div>;
  }
  if (computed == null) {
    return <div className="text-muted small">{t('spray.sessions.waterVolumeReadout.deferredSingle')}</div>;
  }
  return (
    <div>
      <span className="pat-field__label">{t('spray.sessions.waterVolumeReadout.label')}: </span>
      <strong>{formatLitres(computed)}</strong>
      <span className="text-muted small ms-2">
        ({t('spray.sessions.waterVolumeReadout.breakdown', { waterPerHectare, areaHectares })})
      </span>
    </div>
  );
}

/**
 * Phase 5 / Task 5.2 — derive the initial ComboInput[] for the form from a
 * loaded session. Precedence:
 *
 *   1. session.combos (populated by GET /sessions/:id once the backend has
 *      materialised one combo per operator via lazy backfill).
 *   2. Legacy parent fields (operatorIds + single machineId). Covers
 *      freshly-loaded edit sessions before backfill runs, and tests that
 *      pass legacy-only fixtures.
 *   3. Create mode — empty array; ComboListEditor renders its empty-state
 *      hint.
 */
function deriveInitialCombos(session?: SpraySession): ComboInput[] {
  if (Array.isArray(session?.combos) && session!.combos!.length > 0) {
    return session!.combos!.map(c => ({
      _id: c._id,
      operatorId:
        typeof c.operatorId === 'string'
          ? c.operatorId
          : (c.operatorId as any)?._id ?? '',
      machineId:
        typeof c.machineId === 'string'
          ? c.machineId
          : (c.machineId as any)?._id ?? '',
      // Phase 6b — preserve per-combo intensity loaded from the server so
      // a save with no UI change to intensity doesn't surface a spurious
      // patch in diffCombos.
      intensityLevel: c.intensityLevel,
      // Phase 6b hotfix — a server-stored intensity is a deliberate choice
      // (either user-picked or previously auto-suggested and saved). Mark
      // it as overridden so the auto-fill effect doesn't silently clobber
      // it with a fresh suggestion on form re-mount when the user reopens
      // the session to edit. Rows with no saved intensity leave the flag
      // unset (falsy) so the effect can suggest a value as it always did.
      intensityUserOverrode: c.intensityLevel != null ? true : undefined,
      // PDF auto-gen — carry regenStatus and instructionId so
      // ComboInstructionCell can render in the editor during planning.
      regenStatus: c.regenStatus,
      instructionId: typeof c.instructionId === 'string' ? c.instructionId : (c.instructionId as any)?._id ?? null,
    }));
  }
  const legacyMachine =
    typeof session?.machineId === 'string'
      ? session.machineId
      : (session?.machineId as any)?._id ?? '';
  const legacyOperators = (session?.operatorIds ?? []).map(op =>
    typeof op === 'string' ? op : (op as any)._id,
  );
  if (legacyOperators.length > 0) {
    return legacyOperators.map(opId => ({
      operatorId: opId,
      machineId: legacyMachine,
    }));
  }
  return [];
}

/**
 * Phase 5 / Task 5.2 — derive legacy `{ operatorIds, machineId }` parent
 * fields from the combos array. The backend's POST /sessions/bulk and
 * PUT /sessions/:id still take the legacy shape; the lazy backfill
 * materialises one combo per operator at first read. Per-combo machine
 * variance at create time is deferred to edit-mode (see save flow note).
 */
function comboBagToLegacyPayload(
  combos: ComboInput[],
): { operatorIds: string[]; machineId: string | undefined } {
  const validCombos = combos.filter(c => c.operatorId && c.machineId);
  const operatorIds = Array.from(new Set(validCombos.map(c => c.operatorId)));
  const machineId = validCombos[0]?.machineId;
  return { operatorIds, machineId };
}

interface ComboEditPatch {
  machineId?: string;
  intensityLevel?: IntensityLabel;
}

interface ComboDiff {
  toAdd: ComboInput[];
  toRemove: string[];
  toEdit: { _id: string; patch: ComboEditPatch }[];
}

/**
 * Diff the user's combos against the loaded `session.combos` to compute
 * the minimal set of addCombo / editCombo / removeCombo calls needed to
 * reconcile server state with the form. Operator identity keys the diff
 * (operator on a combo is immutable — to swap operators the user removes
 * the existing combo and adds a new one).
 *
 * Phase 6b / Task 6b.12 — the edit branch now emits a `patch` object so a
 * single editCombo call can carry either `machineId`, `intensityLevel`,
 * or both. We only set a key on `patch` when its value actually changed,
 * so rows where only the machine moved don't drag along a redundant
 * `intensityLevel: undefined` payload and vice-versa.
 */
function diffCombos(
  existing: OperatorSpraySessionCombo[],
  next: ComboInput[],
): ComboDiff {
  const existingByOperator = new Map(existing.map(c => [c.operatorId, c]));
  const nextValid = next.filter(c => c.operatorId && c.machineId);
  const nextOperatorIds = new Set(nextValid.map(c => c.operatorId));
  const toAdd: ComboInput[] = [];
  const toEdit: { _id: string; patch: ComboEditPatch }[] = [];
  for (const n of nextValid) {
    const match = existingByOperator.get(n.operatorId);
    if (!match) {
      toAdd.push(n);
      continue;
    }
    const patch: ComboEditPatch = {};
    if (match.machineId !== n.machineId) {
      patch.machineId = n.machineId;
    }
    // Comparison normalises null → undefined so a row whose intensity
    // is genuinely absent on both sides doesn't flag as a diff. Note:
    // `existing.intensityLevel` is the freshly-fetched server value
    // (Phase 6b backend stores intensity per-combo), so the comparison
    // is meaningful as soon as the server has materialised one.
    const existingIntensity = (match.intensityLevel ?? undefined) as IntensityLabel | undefined;
    const nextIntensity = (n.intensityLevel ?? undefined) as IntensityLabel | undefined;
    if (existingIntensity !== nextIntensity) {
      patch.intensityLevel = nextIntensity;
    }
    if (Object.keys(patch).length > 0) {
      toEdit.push({ _id: match._id, patch });
    }
  }
  const toRemove = existing
    .filter(c => !nextOperatorIds.has(c.operatorId))
    .map(c => c._id);
  return { toAdd, toRemove, toEdit };
}

const SpraySessionForm: React.FC<Props> = ({ session, prefilledProgramId, onSave, onCancel }) => {
  const { t } = useTranslation();
  const isEdit = !!session;

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.sessions.title'), to: '/protection/sessions' },
    { label: isEdit
        ? t('spray.sessions.editTitle', { name: session?.name ?? '' })
        : t('spray.sessions.newTitle', { defaultValue: 'New session' }) },
  ]);

  const [programs, setPrograms] = useState<SprayProgram[]>([]);
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [operators, setOperators] = useState<SprayOperator[]>([]);
  // Catalogue used by the Planning Summary card on the Edit Page (Phase 2 / Task 4).
  const [products, setProducts] = useState<SprayProduct[]>([]);

  const [name, setName] = useState(session?.name ?? '');
  const [nameTouched, setNameTouched] = useState(!!session?.name);
  const [date, setDate] = useState(session?.date?.substring(0, 10) ?? '');
  const [programId, setProgramId] = useState(
    (typeof session?.programId === 'string' ? session.programId : session?.programId?._id) ?? prefilledProgramId ?? ''
  );
  const [roundId, setRoundId] = useState<string>(session?.roundId ?? '');
  const [comments, setComments] = useState(session?.comments ?? '');
  const [orchardIds, setOrchardIds] = useState<string[]>(session?.orchardIds ?? []);
  // Phase 5 / Task 5.2 — combo bag replaces the parallel
  // `machineId: string` + `operatorIds: string[]` fields. The save flow
  // diffs against `session.combos` (when present) and writes the legacy
  // parent shape for the bulk-create / update endpoints.
  const [combos, setCombos] = useState<ComboInput[]>(deriveInitialCombos(session));
  const [combosValid, setCombosValid] = useState<boolean>(true);
  // Phase 6b / Task 6b.11 — intensity moved from a session-level select to
  // a per-combo column inside ComboListEditor. The form no longer owns the
  // intensityLevel field, never sends it on the parent payload, and reads
  // each combo's intensity from `combos[i].intensityLevel` when needed.
  // Status is derived server-side (spray_session_workflow_20260427 / Phase 1).
  // The form no longer lets the user set it; we display whatever the server
  // last persisted, defaulting to 'draft' for a new session.
  const status = session?.status ?? 'draft';

  // Track: spray_session_ui_changes_20260502 / Phase 3 / Task 1.
  // Field-locking decision: Program / Round are always read-only on edit
  // (already enforced); Name and PPE Checklist additionally become
  // read-only when the loaded session is in the terminal-ish status
  // bucket. PPE Checklist isn't an editable input on this form today,
  // so the boolean below drives the Name field; if PPE editing UI lands
  // here later, gate it on the same flag.
  const lockNameAndPpe = isEdit && TERMINAL_ISH_STATUSES.includes(status);

  // Track: spray_session_ui_changes_20260502 / Phase 3 / Task 2.
  // Belt-and-braces read-only fallback: if a non-admin reaches the Edit
  // Page on a terminal-ish session through any path other than the
  // role-gated Edit button, render the form read-only rather than
  // letting them silently issue a write the backend would accept. The
  // SpraySessionDetail Edit-button visibility check (Phase 1 Task 4)
  // remains the primary user-facing gate.
  const { user } = useAuth();
  const formReadOnly =
    isEdit && TERMINAL_ISH_STATUSES.includes(status) && !canEditTerminalSpraySession(user);

  const [dateError, setDateError] = useState('');
  const [programError, setProgramError] = useState('');
  const [roundError, setRoundError] = useState('');
  const [orchardsError, setOrchardsError] = useState('');
  // Phase 3 / Task 3 — per-status mandatory validation surfaces an inline
  // error under the ComboListEditor when the user has no complete combo
  // (operator + machine pair). The earlier separate machine vs operator
  // errors collapsed into one when Phase 5 / Task 5.2 moved both fields
  // into the same per-row editor.
  const [operatorsError, setOperatorsError] = useState('');
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [generatingCount, setGeneratingCount] = useState(0);
  // PDF auto-gen — track in-flight manual regen/delete calls so the cell
  // can optimistically render the spinner state without waiting for the
  // backend to flip regenStatus to 'regenerating' (which only happens on
  // the async form-save / waterVolume-change paths, not manual button clicks).
  const [inFlightComboActions, setInFlightComboActions] = useState<Set<string>>(new Set());
  // Phase 6b post-ship hotfix — when the save flow detects that a row's
  // (machineId, intensityLevel) pair would not pass the backend's
  // _validateIntensityOnMachine check, it drops `intensityLevel` from the
  // outbound patch and records a warning here. Surfaced as an inline alert
  // above the SaveBar after save completes so the user can re-pick a valid
  // intensity for the affected row(s) without a 422 round-trip.
  const [staleIntensityWarnings, setStaleIntensityWarnings] = useState<
    Array<{ rowIndex: number; intensityLevel: IntensityLabel; machineName: string }>
  >([]);
  // Track: spray_session_ui_changes_20260502 / Phase 3 / Task 4.
  // Orchard-change modal state. Fires when the user picks a different
  // primary orchard from the loaded session's. Asks whether to overwrite
  // the Name field with the canonical three-part form
  //   `${programName} - ${roundName} - ${orchardName}`
  // (round segment dropped on legacy rounds without a name on disk) or
  // keep the current name. The orchard change is already applied to
  // form state at the time the modal opens.
  const [showOrchardChangeModal, setShowOrchardChangeModal] = useState(false);
  const [pendingOrchardName, setPendingOrchardName] = useState<string>('');

  // Cancel Session modal state — Phase 3 / Task 2.
  const hasSessionsAccess = useMenu('spraying:sessions');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const canCancelSession =
    isEdit &&
    !!session &&
    !TERMINAL_STATUSES.includes(session.status ?? '') &&
    hasSessionsAccess;

  // Dirty-tracking bag. The initial values mirror the useState initializers
  // above 1:1 so a freshly-loaded form is pristine (dirty === false). Only
  // user-editable fields belong here; fetched catalogues, error flags,
  // modal-visibility flags, the cancel-session modal state, derived
  // `status`, and the `nameTouched` touch-tracker are intentionally
  // excluded.
  const { dirty } = useFormDirty(
    {
      name: session?.name ?? '',
      date: session?.date?.substring(0, 10) ?? '',
      programId:
        (typeof session?.programId === 'string'
          ? session.programId
          : session?.programId?._id) ?? prefilledProgramId ?? '',
      roundId: session?.roundId ?? '',
      comments: session?.comments ?? '',
      orchardIds: session?.orchardIds ?? [],
      // Combo bag replaces parallel machineId/operatorIds entries
      // (Phase 5 / Task 5.2). Initial derivation matches state init so
      // a freshly-loaded form is pristine.
      combos: deriveInitialCombos(session),
    },
    { name, date, programId, roundId, comments, orchardIds, combos },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  useEffect(() => {
    sprayService.getPrograms().then(setPrograms).catch(() => {});
    sprayService.getOrchards().then(setOrchards).catch(() => {});
    sprayService.getMachines().then(setMachines).catch(() => {});
    sprayService.getOperators().then(setOperators).catch(() => {});
    sprayService.getProducts().then(setProducts).catch(() => {});
  }, []);

  // Task 2.7 — crop type tree for cross-checking orchard vs program crop.
  const [cropTypes, setCropTypes] = useState<CropTypeNode[]>([]);
  useEffect(() => {
    listGlobalCropTypes().then(setCropTypes).catch(() => setCropTypes([]));
  }, []);

  // Derived once per render; cheap and lets the JSX/handlers reference one
  // source of truth for the selected program and its sorted rounds.
  const selectedProgram = programs.find(p => p._id === programId);
  const availableRounds = sortRounds(selectedProgram?.rounds ?? []);
  const selectedRound = availableRounds.find(r => r._id === roundId);

  // Phase 5.2 / Phase 6b — `selectedMachine` is the FIRST combo's machine.
  // The CalibrationStatusBadge, the tank-volume readout, and the edit-mode
  // PlanningSummary card all key off this single derived value. Per-row
  // intensity suggestion logic moved into ComboListEditor (Task 6b.10).
  const selectedMachine = useMemo(() => {
    const firstMachineId = combos[0]?.machineId;
    return firstMachineId
      ? machines.find(m => m._id === firstMachineId) ?? null
      : null;
  }, [combos, machines]);

  const selectedOrchard = useMemo(() => {
    if (orchardIds.length !== 1) return null;
    return orchards.find(o => o._id === orchardIds[0]) || null;
  }, [orchards, orchardIds]);

  // Task 2.7 — advisory crop mismatch: warn when the orchard's crop is not
  // within the program's crop lineage. Non-blocking — never disables submit.
  const cropMismatch = useMemo(() => {
    const programCrop = selectedProgram?.cropType;
    const orchardCrop = selectedOrchard?.cropType;
    if (!programCrop || !orchardCrop) return false;
    return !lineageOf(orchardCrop, cropTypes).includes(programCrop);
  }, [selectedProgram?.cropType, selectedOrchard?.cropType, cropTypes]);

  const computedWaterVolume = useMemo(() => {
    if (orchardIds.length !== 1) return null;
    if (!selectedRound?.waterPerHectare || selectedRound.waterPerHectare <= 0) return null;
    if (!selectedOrchard?.areaHectares || selectedOrchard.areaHectares <= 0) return null;
    return selectedOrchard.areaHectares * selectedRound.waterPerHectare;
  }, [orchardIds.length, selectedRound?.waterPerHectare, selectedOrchard?.areaHectares]);

  // PDF auto-gen — reload combos from the server and update local state.
  // Memoised with useCallback so the polling effect's dependency array stays
  // stable and the interval is not re-created on every render.
  const reloadCombos = useCallback(async () => {
    if (!session) return;
    try {
      const refreshed = await sprayService.getSession(session._id);
      setCombos(deriveInitialCombos({ ...refreshed.session, combos: refreshed.combos }));
    } catch {
      // Swallow polling errors silently — a transient network hiccup should
      // not surface an error banner; the next tick will retry.
    }
  }, [session]);

  // PDF auto-gen — live polling while any combo is in the 'regenerating' state.
  // The interval clears as soon as all combos flip to 'idle' or 'failed'.
  useEffect(() => {
    const anyRegenerating = combos.some(c => c.regenStatus === 'regenerating');
    if (!anyRegenerating) return;
    const id = setInterval(() => {
      void reloadCombos();
    }, 2500);
    return () => clearInterval(id);
  }, [combos, reloadCombos]);

  // PDF auto-gen — optimistic in-flight helpers. Add a comboId to the set
  // before the async call so the cell immediately renders the spinner; remove
  // it in the finally block so the cell reverts to its real regenStatus once
  // the server round-trip completes.
  const startComboAction = (comboId: string) => {
    setInFlightComboActions(prev => {
      const next = new Set(prev);
      next.add(comboId);
      return next;
    });
  };
  const endComboAction = (comboId: string) => {
    setInFlightComboActions(prev => {
      const next = new Set(prev);
      next.delete(comboId);
      return next;
    });
  };

  // PDF auto-gen — handlers for ComboInstructionCell rendered inside the editor.
  // Errors surface via the existing apiError banner. After mutating server state
  // the combo bag is refreshed by re-fetching the session and re-deriving the
  // combo inputs so regenStatus / instructionId stay in sync.
  const handleComboView = async (comboId: string) => {
    const combo = combos.find(c => c._id === comboId);
    if (!combo?.instructionId) return;
    try {
      const url = await sprayService.getInstructionSignedUrl(combo.instructionId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setApiError(err.message || t('spray.combo.pdf.viewFailed'));
    }
  };

  const handleComboRegenerate = async (comboId: string) => {
    startComboAction(comboId);
    try {
      const { pdfUrl } = await sprayService.regenerateComboInstruction(comboId);
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      await reloadCombos();
    } catch (err: any) {
      setApiError(err.message || t('spray.combo.pdf.regenFailed'));
    } finally {
      endComboAction(comboId);
    }
  };

  const handleComboDelete = async (comboId: string) => {
    startComboAction(comboId);
    try {
      await sprayService.deleteComboInstruction(comboId);
      await reloadCombos();
    } catch (err: any) {
      setApiError(err.message || t('spray.combo.pdf.deleteFailed'));
    } finally {
      endComboAction(comboId);
    }
  };

  const handleProgramChange = (id: string) => {
    setProgramId(id);
    // Round-link is program-scoped; resetting prevents an orphaned roundId
    // from leaking into the payload after the user pivots to a different
    // program.
    setRoundId('');
    setRoundError('');
    if (!nameTouched) {
      const prog = programs.find(p => p._id === id);
      if (prog) setName(composeSessionName([prog.name]));
    }
  };

  const handleRoundChange = (id: string) => {
    setRoundId(id);
    setRoundError('');
    // Auto-extend the session name to "{program.name} - {round.name}"
    // in create mode. Backend bulk-create then appends "- {orchard}" per
    // generated session, completing the canonical three-part form.
    if (!nameTouched && selectedProgram) {
      const r = availableRounds.find(rr => rr._id === id);
      if (r) {
        setName(composeSessionName([selectedProgram.name, r.name]));
      }
    }
  };

  // Track: spray_session_ui_changes_20260502 / Phase 3 / Task 4.
  // Wraps setOrchardIds to fire the orchard-change rename prompt when the
  // user makes a positive orchard selection that differs from what was
  // displayed immediately before the change. Comparing against the
  // previous state (rather than only against the loaded session) makes
  // the trigger fire reliably for the clear-then-pick path —
  // user-reported regression on 2026-05-03 — without changing the
  // single-step orchard-swap behaviour.
  const handleOrchardChange = (newIds: string[]) => {
    const prevPrimary = orchardIds[0];
    setOrchardIds(newIds);
    if (!isEdit || !session) return;
    const newPrimary = newIds[0];
    // Clearing the picker is not a swap; the generated name would have
    // a missing orchard segment.
    if (!newPrimary) return;
    // Re-selecting the same orchard that was just there is a no-op.
    if (newPrimary === prevPrimary) return;
    const next = orchards.find(o => o._id === newPrimary);
    if (!next) return;
    setPendingOrchardName(next.orchardName);
    setShowOrchardChangeModal(true);
  };

  const acceptOrchardRename = () => {
    const programName = selectedProgram?.name || '';
    if (programName && pendingOrchardName) {
      // Canonical three-part name: "{program} - {round} - {orchard}".
      // Rounds without a name on disk collapse to "{program} - {orchard}"
      // via composeSessionName's empty-segment filter.
      setName(composeSessionName([programName, selectedRound?.name, pendingOrchardName]));
      setNameTouched(true);
    }
    setShowOrchardChangeModal(false);
    setPendingOrchardName('');
  };

  const dismissOrchardRename = () => {
    setShowOrchardChangeModal(false);
    setPendingOrchardName('');
  };

  const handleSave = async (opts?: { silent?: boolean }) => {
    setDateError('');
    setProgramError('');
    setRoundError('');
    setOrchardsError('');
    setOperatorsError('');
    setApiError('');
    setStaleIntensityWarnings([]);

    if (!date) {
      setDateError(t('spray.sessions.dateRequired'));
      return;
    }

    // Phase 5 / Task 5.2 — combo invalidity (duplicate operator) blocks
    // save in both create and edit modes. ComboListEditor surfaces the
    // inline per-row error already; this guard just refuses to submit.
    if (!combosValid) {
      return;
    }

    if (!isEdit) {
      let hasError = false;
      if (!programId) {
        setProgramError(t('spray.sessions.programRequired'));
        hasError = true;
      }
      // Round becomes required as soon as a program is chosen; the server
      // contract enforces this on POST/bulk and a missing roundId would
      // otherwise surface as an opaque 400. Q1 of the design grill.
      if (programId && !roundId) {
        setRoundError(t('spray.sessions.roundRequired'));
        hasError = true;
      }
      if (orchardIds.length === 0) {
        setOrchardsError(t('spray.sessions.orchardsRequired'));
        hasError = true;
      }
      if (hasError) return;
    } else {
      // Track: spray_session_ui_changes_20260502 / Phase 3 / Task 3.
      // Per-status mandatory validation. The form snapshots the loaded
      // status and refuses to submit if any field required for that
      // status is blank — preventing accidental status demotion.
      //   draft        → Orchard
      //   planned      → Orchard, Machine, ≥1 Operator
      //   terminal-ish → Date, Orchard, Machine, ≥1 Operator
      // (Date is already enforced unconditionally above for everyone.)
      let hasError = false;
      if (orchardIds.length === 0) {
        setOrchardsError(t('spray.sessions.orchardsRequired'));
        hasError = true;
      }
      const requiresMachineAndOperator =
        status === 'planned' || TERMINAL_ISH_STATUSES.includes(status);
      if (requiresMachineAndOperator) {
        // A complete combo = both operator AND machine present.
        // ComboListEditor empty / missing-machine row degrades to the
        // single "operators required" error so we don't surface two
        // overlapping messages for the same root cause.
        const anyCompleteCombo = combos.some(c => c.operatorId && c.machineId);
        if (!anyCompleteCombo) {
          setOperatorsError(t('spray.sessions.operatorsRequired'));
          hasError = true;
        }
      }
      if (hasError) return;
    }

    setSaving(true);
    try {
      const { operatorIds, machineId } = comboBagToLegacyPayload(combos);
      if (isEdit) {
        const updated = await sprayService.updateSession(session!._id, {
          programId,
          name: name.trim() || undefined,
          orchardIds,
          date,
          comments: comments || undefined,
          machineId: machineId || undefined,
          operatorIds,
        });

        // Phase 5 / Task 5.2 — reconcile per-combo machines against the
        // server's current combo set. The parent PUT above writes the
        // legacy fields (so the deprecated read paths stay coherent); the
        // combo CRUD calls below propagate the per-row machine choices
        // that the legacy single-machineId shape cannot express.
        //
        // We fetch combos fresh via getSession() rather than trusting
        // `session?.combos` from the prop: the session may have been
        // loaded from a list endpoint that doesn't populate `.combos`,
        // and the GET path also triggers the lazy backfill so the diff
        // operates on a consistent server-materialised view. Without
        // this, list-loaded edits silently dropped the per-row machine
        // selection because the reconciliation branch was skipped.
        const refreshed = await sprayService.getSession(session!._id);
        const existingCombos = refreshed.combos ?? [];
        const diff = diffCombos(existingCombos, combos);

        // Phase 6b post-ship hotfix — sanitize each outbound combo payload
        // against the row's machine calibration. If the intensity label is
        // stale (e.g. left over from a different machine and never reset
        // by the user) we drop it from the payload so the backend's
        // _validateIntensityOnMachine doesn't 422 the whole save. The
        // dropped value is recorded as a warning for inline surfacing.
        const warnings: Array<{ rowIndex: number; intensityLevel: IntensityLabel; machineName: string }> = [];
        for (const c of diff.toAdd) {
          const machine = machines.find(m => m._id === c.machineId);
          const valid = isIntensityOnMachineCalibration(machine, c.intensityLevel);
          let intensityLevel = c.intensityLevel;
          if (!valid && c.intensityLevel) {
            const rowIndex = combos.findIndex(local => local.operatorId === c.operatorId);
            warnings.push({
              rowIndex: rowIndex >= 0 ? rowIndex : 0,
              intensityLevel: c.intensityLevel,
              machineName: machine?.name ?? c.machineId,
            });
            intensityLevel = undefined;
          }
          // Phase 6b / Task 6b.12 — per-row intensity flows through here.
          // The backend's addCombo validator accepts an optional
          // intensityLevel; passing undefined falls back to the parent
          // session's intensity at resolve time (resolveComboIntensity).
          await sprayService.addCombo(session!._id, {
            operatorId: c.operatorId,
            machineId: c.machineId,
            intensityLevel,
          });
        }
        for (const c of diff.toEdit) {
          let patch = c.patch;
          if (patch.intensityLevel) {
            // Patch may carry just `intensityLevel`, in which case the
            // effective machine is whatever the server currently has.
            // Resolve to the patched machineId when present, else the
            // existing server-side machineId for this combo.
            const existingCombo = existingCombos.find(ec => ec._id === c._id);
            const effectiveMachineId = patch.machineId ?? existingCombo?.machineId;
            const machine = machines.find(m => m._id === effectiveMachineId);
            const valid = isIntensityOnMachineCalibration(machine, patch.intensityLevel);
            if (!valid) {
              const rowIndex = combos.findIndex(
                local => existingCombo && local.operatorId === existingCombo.operatorId,
              );
              warnings.push({
                rowIndex: rowIndex >= 0 ? rowIndex : 0,
                intensityLevel: patch.intensityLevel,
                machineName: machine?.name ?? effectiveMachineId ?? '',
              });
              // Drop intensityLevel from the patch; keep machineId if it
              // was also present. If only intensityLevel was on the patch
              // the editCombo call becomes a no-op (empty patch), so skip.
              const { intensityLevel: _dropped, ...rest } = patch;
              patch = rest;
              if (Object.keys(patch).length === 0) continue;
            }
          }
          await sprayService.editCombo(c._id, patch);
        }
        for (const id of diff.toRemove) {
          await sprayService.removeCombo(id);
        }

        if (warnings.length > 0) setStaleIntensityWarnings(warnings);

        // Silent mode — stay on the form so the user can watch PDF regen.
        // Reload combos from the server (the new combo now has an _id and
        // regenStatus='regenerating') so the existing polling picks it up.
        if (opts?.silent) {
          await reloadCombos();
          return;
        }

        onSave(updated);
      } else {
        const resolvedProgram = programs.find(p => p._id === programId);
        const programName = (name.trim() || resolvedProgram?.name || '').trim();
        setGeneratingCount(orchardIds.length);
        Sentry.addBreadcrumb({
          category: 'spray.sessions.bulk',
          message: 'Creating bulk spray sessions from session form',
          level: 'info',
          data: { programId, roundId, orchardCount: orchardIds.length },
        });
        const created = await sprayService.createSessionsBulk({
          programId,
          roundId,
          programName,
          orchardIds,
          date,
          comments: comments || undefined,
          machineId: machineId || undefined,
          operatorIds,
        });

        // Phase 5 — per-combo machine reconciliation for newly-created
        // sessions. POST /sessions/bulk only accepts a single `machineId`,
        // so the lazy backfill materialises every combo with the parent's
        // single machineId. For each created session, refetch combos
        // (triggering the lazy backfill) and editCombo to fix combos whose
        // user-selected machine differs from `combos[0].machineId`.
        const hasMixedMachines = combos.some(c => c.machineId && c.machineId !== machineId);
        if (hasMixedMachines) {
          for (const newSession of created) {
            const refreshed = await sprayService.getSession(newSession._id);
            const serverCombos = refreshed.combos ?? [];
            for (const local of combos) {
              if (!local.operatorId || !local.machineId) continue;
              const serverCombo = serverCombos.find(
                c => String(c.operatorId) === local.operatorId,
              );
              if (serverCombo && String(serverCombo.machineId) !== local.machineId) {
                await sprayService.editCombo(serverCombo._id, {
                  machineId: local.machineId,
                });
              }
            }
          }
        }

        onSave(created);
      }
    } catch (err: any) {
      setApiError(err.message ?? t('spray.sessions.saveError'));
    } finally {
      setSaving(false);
      setGeneratingCount(0);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formReadOnly) return;
    void handleSave();
  };

  // Hoisted section titles — TOC entries and FormSection titles share one
  // source of truth (mirrors SprayProductForm + SprayProgramForm
  // convention).
  const titles = {
    details: t('spray.sessions.sections.details', { defaultValue: 'Session details' }),
    orchards: t('spray.sessions.sections.orchards', { defaultValue: 'Orchards' }),
    execution: t('spray.sessions.sections.execution', { defaultValue: 'Execution' }),
    notes: t('spray.sessions.sections.notes', { defaultValue: 'Notes' }),
    summary: t('spray.sessions.sections.summary', { defaultValue: 'Planning summary' }),
  };

  // Build the section list dynamically. Planning Summary only renders in
  // edit mode, so the TOC must reflect that. Section ids are prefixed
  // `session-` to avoid collisions with the input ids (`session-name`,
  // `session-date`, etc.).
  const sectionDefs = useMemo<FormShellSection[]>(() => {
    const base: FormShellSection[] = [
      { id: 'session-details', title: titles.details },
      { id: 'session-orchards', title: titles.orchards },
      { id: 'session-execution', title: titles.execution },
      { id: 'session-notes', title: titles.notes },
    ];
    if (isEdit) base.push({ id: 'session-summary', title: titles.summary });
    return base;
  }, [isEdit, titles.details, titles.orchards, titles.execution, titles.notes, titles.summary]);

  // In read-only mode there is no save action — primaryAction is omitted and
  // Close moves to secondaryActions so the user can still leave the form.
  // In normal mode the primary is "Save" with a "Cancel" secondary.
  const primaryAction: SaveBarAction | undefined = formReadOnly
    ? undefined
    : {
        label: saving
          ? t('patterns.saveBar.primary.saving')
          : apiError
            ? t('patterns.saveBar.primary.retry')
            : t('patterns.saveBar.primary.save'),
        onClick: handleSave,
        disabled: (!dirty && !apiError) || saving,
      };
  const secondaryActions: SaveBarAction[] = formReadOnly
    ? [{ label: t('common.close'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); } }]
    : [{ label: t('common.cancel'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); }, disabled: saving }];

  // In read-only mode the status area is forced empty — there is no save
  // action so dirty/saving/error states are not meaningful. The dirty hook
  // still runs (cannot conditionally call it) but its result is ignored.
  const saveBarStatus: SaveBarStatus = formReadOnly
    ? null
    : saving
      ? { kind: 'saving' }
      : apiError
        ? { kind: 'error', message: apiError }
        : dirty
          ? { kind: 'dirty' }
          : null;

  const headerTitle = isEdit
    ? t('spray.sessions.editTitle', { name: session?.name ?? '', defaultValue: 'Edit {{name}}' })
    : t('spray.sessions.newTitle', { defaultValue: 'New session' });

  const headerActions = canCancelSession ? (
    <button
      type="button"
      className="pat-btn pat-btn--danger"
      onClick={() => {
        setCancelError('');
        setShowCancelModal(true);
      }}
    >
      {t('spray.sessions.cancelSession')}
    </button>
  ) : undefined;

  return (
    <div data-testid="spray-session-form">
      <PageHeader title={headerTitle} actions={headerActions} />

      {apiError && (
        <Alert variant="danger" role="alert" className="mb-3">
          {apiError}
        </Alert>
      )}

      {staleIntensityWarnings.length > 0 && (
        <Alert
          variant="warning"
          role="alert"
          className="mb-3"
          data-testid="stale-intensity-warning"
        >
          <div>{t('spray.sessions.combos.intensity.staleHeader')}</div>
          <ul className="mb-0">
            {staleIntensityWarnings.map((w, i) => (
              <li key={`stale-${i}-${w.rowIndex}`}>
                {t('spray.sessions.combos.intensity.staleRow', {
                  row: w.rowIndex + 1,
                  intensity: w.intensityLevel,
                  machine: w.machineName,
                })}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {formReadOnly && (
        <Alert variant="warning" className="mb-3">
          {t('spray.sessions.terminalishReadOnly')}
        </Alert>
      )}

      <FormShell
        sections={sectionDefs}
        onSubmit={handleSubmit}
        saveBar={
          <SaveBar
            status={saveBarStatus}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
          />
        }
      >
        {/*
          Wrapping all sections in a single <fieldset disabled> propagates
          the disabled state to every native form control (input, select,
          textarea, button) inside it. We render the fieldset with no
          visual styling so it doesn't disturb FormShell's layout.
        */}
        <fieldset disabled={formReadOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <FormSection id="session-details" title={titles.details}>
            <div className="pat-field-grid pat-field-grid--2">
              <Field
                label={t('spray.sessions.sessionName')}
                controlId="session-name"
              >
                <input
                  id="session-name"
                  type="text"
                  className="pat-input"
                  value={name}
                  placeholder={t('spray.sessions.sessionNamePlaceholder')}
                  readOnly={lockNameAndPpe}
                  onChange={e => { setName(e.target.value); setNameTouched(true); }}
                />
              </Field>
              <Field
                label={t('spray.sessions.date')}
                controlId="session-date"
                errorText={dateError || undefined}
              >
                <input
                  id="session-date"
                  type="date"
                  className="pat-input"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  aria-invalid={Boolean(dateError) || undefined}
                />
              </Field>
            </div>
            <div className="pat-field-grid pat-field-grid--2">
              <Field
                label={t('spray.sessions.program')}
                controlId={isEdit ? undefined : 'session-program'}
                errorText={programError || undefined}
              >
                {isEdit ? (
                  <input
                    type="text"
                    className="pat-input"
                    data-testid="session-program-readonly"
                    value={selectedProgram?.name ?? '—'}
                    readOnly
                  />
                ) : (
                  <select
                    id="session-program"
                    className="pat-input"
                    value={programId}
                    onChange={e => handleProgramChange(e.target.value)}
                    aria-invalid={Boolean(programError) || undefined}
                  >
                    <option value="">—</option>
                    {programs.map(p => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                )}
              </Field>
              {/*
               * Round selection.
               *
               * Create mode (Q1, Q5, Q6 of the design grill):
               *   - Visible only when a program is selected.
               *   - When the program has zero rounds, the dropdown is suppressed
               *     and a warning is rendered in its place.
               *   - Required: client-side validation matches the server contract.
               *
               * Edit mode: rendered as a read-only display via the side panel
               * below; the PUT route does not accept roundId so an editable
               * dropdown would lie about persistence.
               */}
              {!isEdit && programId && availableRounds.length > 0 && (
                <Field
                  label={t('spray.sessions.round')}
                  controlId="session-round"
                  errorText={roundError || undefined}
                >
                  <select
                    id="session-round"
                    className="pat-input"
                    value={roundId}
                    onChange={e => handleRoundChange(e.target.value)}
                    aria-invalid={Boolean(roundError) || undefined}
                  >
                    <option value="">—</option>
                    {availableRounds.map(r => (
                      <option key={r._id} value={r._id}>{formatRoundOptionLabel(r)}</option>
                    ))}
                  </select>
                </Field>
              )}
              {!isEdit && programId && availableRounds.length === 0 && (
                <div className="text-warning small" role="status">
                  {t('spray.sessions.programHasNoRounds')}
                </div>
              )}
              {isEdit && programId && (
                <Field label={t('spray.sessions.round')}>
                  {selectedRound ? (
                    <input
                      type="text"
                      className="pat-input"
                      value={
                        typeof selectedRound.seq === 'number'
                          ? `R${selectedRound.seq}${selectedRound.name ? ` — ${selectedRound.name}` : ''}`
                          : (selectedRound.name || selectedRound.plannedDate)
                      }
                      readOnly
                    />
                  ) : (
                    <Badge bg="warning" text="dark" pill data-testid="legacy-session-round">
                      {t('spray.sessions.legacySessionRound')}
                    </Badge>
                  )}
                </Field>
              )}
            </div>
          </FormSection>

          <FormSection
            id="session-orchards"
            title={t(isEdit ? 'spray.sessions.orchard' : 'spray.sessions.orchards')}
          >
            <OrchardPicker
              orchards={orchards}
              selected={orchardIds}
              onChange={handleOrchardChange}
              singleSelect={isEdit}
            />
            {cropMismatch && selectedOrchard?.cropType && selectedProgram?.cropType && (
              <Notice tone="warn" className="mt-2">
                {t('spray.sessions.cropMismatchWarning', {
                  orchard: selectedOrchard.cropType,
                  program: selectedProgram.cropType,
                })}
              </Notice>
            )}
            {orchardsError && (
              <div className="text-danger small mt-1" role="alert">{orchardsError}</div>
            )}
          </FormSection>

          <FormSection id="session-execution" title={titles.execution}>
            {/*
              Phase 5 / Task 5.2 — single ComboListEditor replaces the
              separate machine select + operators checkbox group. The
              editor owns per-(operator × machine) assignments; the form
              still writes the legacy parent fields (operatorIds, first
              combo's machineId) so the deprecated read paths stay coherent
              alongside the new combo CRUD reconciliation.
            */}
            {/*
              Phase 6b / Task 6b.11 — `selectedRound` is plumbed so the
              per-row intensity suggestion (Task 6b.10) can read the
              round's `waterPerHectare`. Task 6b.12 extends the save flow
              to actually persist per-row intensity through diffCombos.
            */}
            <ComboListEditor
              value={combos}
              onChange={setCombos}
              operators={operators}
              machines={machines}
              selectedRound={selectedRound}
              disabled={formReadOnly}
              onValidityChange={setCombosValid}
              renderRowActions={(combo) => {
                if (combo._id) {
                  // Saved combo — PDF auto-gen cell.
                  // While a manual regen or delete is in flight, synthesise a
                  // 'regenerating' regenStatus so the cell shows the spinner
                  // immediately, without waiting for the backend to flip its own
                  // regenStatus (which only happens on async save paths).
                  const effectiveRegenStatus = inFlightComboActions.has(combo._id)
                    ? 'regenerating' as const
                    : combo.regenStatus;
                  return (
                    <ComboInstructionCell
                      combo={{
                        _id: combo._id,
                        // Editor-visible combos are by definition in a non-terminal
                        // state (planned/draft). Hard-coding 'not_started' here is
                        // safe: the backend re-validates the actual status on every
                        // API call, so this is a UI-only signal for the lock predicate.
                        status: 'not_started',
                        regenStatus: effectiveRegenStatus,
                        instructionId: combo.instructionId,
                      }}
                      parentStatus={session?.status ?? 'planned'}
                      onView={handleComboView}
                      onRegenerate={handleComboRegenerate}
                      onDelete={handleComboDelete}
                    />
                  );
                }
                // Unsaved row with operator + machine filled — offer a "Generate"
                // shortcut that saves the form. The backend's addCombo endpoint
                // fires the async PDF regen, so once saved the row flips to
                // regenStatus='regenerating' and the polling picks it up.
                if (combo.operatorId && combo.machineId) {
                  return (
                    <button
                      type="button"
                      className="pat-btn pat-btn--outline"
                      onClick={() => { void handleSave({ silent: true }); }}
                      disabled={saving}
                      aria-label={t('spray.combo.pdf.generate')}
                      title={t('spray.combo.pdf.generate')}
                    >
                      {saving
                        ? <Spinner size="sm" animation="border" />
                        : <i className="bi bi-file-earmark-pdf-fill" aria-hidden="true" />}
                    </button>
                  );
                }
                return null;
              }}
            />
            {operatorsError && (
              <div className="text-danger small mt-2" role="alert">{operatorsError}</div>
            )}
            {/*
              Phase 6b / Task 6b.11 — the session-level intensity select
              has moved to a per-row column on the ComboListEditor above.
              Status remains the only field in this row; the grid layout
              keeps Status aligned with the rest of the page's two-column
              grids.
            */}
            <div className="pat-field-grid pat-field-grid--2 mt-3">
              <Field label={t('spray.sessions.status')}>
                <div>
                  <Badge
                    bg={STATUS_VARIANT[status] ?? 'secondary'}
                    data-testid="status-badge"
                  >
                    {t(STATUS_I18N_KEY[status] ?? 'spray.sessions.status')}
                  </Badge>
                </div>
              </Field>
            </div>
            <div className="mt-2" data-testid="water-volume-readout">
              {renderWaterVolumeReadout(t, orchardIds, computedWaterVolume, selectedRound?.waterPerHectare, selectedOrchard?.areaHectares)}
            </div>
          </FormSection>

          <FormSection id="session-notes" title={titles.notes}>
            <Field
              label={t('spray.sessions.comments')}
              controlId="session-comments"
            >
              <textarea
                id="session-comments"
                rows={2}
                className="pat-input"
                value={comments}
                onChange={e => setComments(e.target.value)}
              />
            </Field>
          </FormSection>

          {/*
            Planning Summary card — Edit-mode only (track
            spray_session_ui_changes_20260502 / Phase 2 / Task 4). Recalculates
            in real-time as orchard / machine selections change because the
            component reads its derived inputs from existing form state.
          */}
          {isEdit && (
            <FormSection id="session-summary" title={titles.summary}>
              <PlanningSummary
                orchard={orchards.find(o => o._id === orchardIds[0]) ?? null}
                round={selectedRound ?? null}
                machine={selectedMachine}
                products={products}
              />
            </FormSection>
          )}

          {generatingCount > 0 && (
            <Alert variant="info" role="status" className="mb-2">
              {t('spray.sessions.generatingSessions', { count: generatingCount })}
            </Alert>
          )}
        </fieldset>
      </FormShell>

      <Modal
        show={showCancelModal}
        onHide={() => {
          if (cancelling) return;
          setShowCancelModal(false);
          setCancelError('');
        }}
        title={t('spray.sessions.cancelSessionTitle')}
      >
        <Modal.Body>
          <p>{t('spray.sessions.cancelSessionWarning')}</p>
          {cancelError && <Alert variant="danger" className="mb-0">{cancelError}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => {
              setShowCancelModal(false);
              setCancelError('');
            }}
            disabled={cancelling}
          >
            {t('spray.sessions.keepSession')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--danger"
            disabled={cancelling}
            onClick={async () => {
              if (!session) return;
              setCancelling(true);
              setCancelError('');
              try {
                const updated = await sprayService.updateSession(session._id, { status: 'cancelled' });
                setShowCancelModal(false);
                onSave(updated);
              } catch (err: any) {
                setCancelError(err.message ?? t('common.error'));
              } finally {
                setCancelling(false);
              }
            }}
          >
            {t('spray.sessions.confirmCancelSession')}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showOrchardChangeModal}
        onHide={dismissOrchardRename}
        title={t('spray.sessions.orchardChangeTitle')}
      >
        <Modal.Body>
          <p className="mb-0">{t('spray.sessions.orchardChangeBody')}</p>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="pat-btn pat-btn--subtle" onClick={dismissOrchardRename}>
            {t('spray.sessions.keepCurrentName')}
          </button>
          <button type="button" className="pat-btn pat-btn--primary" onClick={acceptOrchardRename}>
            {t('spray.sessions.updateName')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SpraySessionForm;
